import React, { useState } from 'react';

export default function StepOutput({ script, onBack, onReset }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(script);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (e) {
      const textarea = document.createElement('textarea');
      textarea.value = script;
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleDownload = () => {
    const blob = new Blob([script], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'mikrotik-config.rsc';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const lines = script.split('\n').filter(l => l.trim() && !l.startsWith('#'));
  const commands = lines.length;

  return (
    <div>
      {/* Success Header */}
      <div className="mb-6 p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-xl">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-green-500 rounded-full flex items-center justify-center">
            <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" /></svg>
          </div>
          <div>
            <h2 className="text-xl font-bold text-green-800 dark:text-green-200">สร้างสคริปต์สำเร็จ!</h2>
            <p className="text-sm text-green-600 dark:text-green-400">{commands} คำสั่ง พร้อมใช้งาน</p>
          </div>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex flex-wrap gap-2 mb-4">
        <button onClick={handleCopy} className={`btn-secondary text-sm flex items-center gap-1.5 ${copied ? 'ring-2 ring-green-400' : ''}`}>
          {copied ? (
            <><svg className="w-4 h-4 text-green-500" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" /></svg>คัดลอกแล้ว!</>
          ) : (
            <><svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" /></svg>คัดลอก</>
          )}
        </button>
        <button onClick={handleDownload} className="btn-primary text-sm flex items-center gap-1.5">
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" /></svg>
          ดาวน์โหลด .rsc
        </button>
      </div>

      {/* Script Preview */}
      <div className="relative mb-6">
        <div className="absolute top-3 right-3 flex gap-1">
          <span className="px-2 py-0.5 bg-gray-700 text-gray-300 text-[10px] rounded font-mono">{commands} commands</span>
        </div>
        <pre className="bg-gray-900 text-green-400 p-4 pt-8 rounded-xl overflow-x-auto text-xs font-mono leading-relaxed max-h-[500px] overflow-y-auto border border-gray-800">
          <code>{script}</code>
        </pre>
      </div>

      {/* How to apply */}
      <div className="mb-6 p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-xl">
        <h4 className="font-bold text-blue-900 dark:text-blue-200 text-sm mb-3 flex items-center gap-2">
          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" /></svg>
          วิธีนำสคริปต์ไปใช้
        </h4>
        <div className="space-y-2 text-sm text-blue-800 dark:text-blue-300">
          <div className="flex items-start gap-2">
            <span className="w-5 h-5 bg-blue-200 dark:bg-blue-800 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 mt-0.5">1</span>
            <span>คัดลอกสคริปต์หรือดาวน์โหลดไฟล์ <code className="bg-blue-100 dark:bg-blue-800 px-1 rounded text-xs">.rsc</code></span>
          </div>
          <div className="flex items-start gap-2">
            <span className="w-5 h-5 bg-blue-200 dark:bg-blue-800 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 mt-0.5">2</span>
            <span>เปิด <strong>WinBox</strong> หรือ <strong>SSH</strong> เข้าสู่ MikroTik Router</span>
          </div>
          <div className="flex items-start gap-2">
            <span className="w-5 h-5 bg-blue-200 dark:bg-blue-800 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 mt-0.5">3</span>
            <span>คลิก <strong>New Terminal</strong> แล้ววางสคริปต์ หรือกด <code className="bg-blue-100 dark:bg-blue-800 px-1 rounded text-xs">/import file-name=mikrotik-config.rsc</code></span>
          </div>
        </div>
      </div>

      {/* Warning */}
      <div className="mb-6 p-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-xl">
        <p className="text-xs text-amber-700 dark:text-amber-300">
          <strong>⚠️ คำเตือน:</strong> สคริปต์นี้จะเปลี่ยนแปลงการตั้งค่า Router ควรสำรองข้อมูลเดิมก่อนใช้งาน หากไม่แน่ใจให้ทดสอบใน Router ทดลองก่อน
        </p>
      </div>

      <div className="flex justify-between">
        <button onClick={onBack} className="btn-secondary">← กลับไปตรวจสอบ</button>
        <button onClick={onReset} className="btn-primary flex items-center gap-2">
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" /></svg>
          เริ่มตั้งค่าใหม่
        </button>
      </div>
    </div>
  );
}
